import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, Button } from "react-daisyui";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import Navbar from "@/layouts/TripLayout/Navbar.tsx";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import type { OnCompleteArgs } from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import shouldCollectBookings from "@/utils/shouldCollectBookings.ts";
import type { TripData } from "$/models/Trip.model.ts";
import { tripCreateSchema } from "$/requests/TripCreateRequest.ts";

const moreInfoSchema = tripCreateSchema
  .pick({
    additional_requests: true,
  })
  .extend({
    additional_requests: z.string().trim().max(1000).optional(),
    trip_specific_changes: z
      .string()
      .trim()
      .max(1000)
      .nullable()
      .optional(),
  });

export type MoreInfoSchema = z.infer<typeof moreInfoSchema>;

type ExtendedTripData = TripData & {
  trip_specific_changes?: string | null;
};

type MoreInfoProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

type SummaryRowProps = {
  label: string;
  value: string;
  isLast?: boolean;
};

function SummaryRow({ label, value, isLast = false }: SummaryRowProps) {
  return (
    <div
      className={`flex flex-col gap-1 px-2 py-3 font-sans text-base sm:flex-row sm:items-center sm:justify-between sm:gap-8 ${
        isLast ? "" : "border-b border-[#191919]/25"
      }`}
    >
      <span className="text-[#8A6D23]">{label}</span>
      <span className="text-[#191919] sm:text-right">{value}</span>
    </div>
  );
}

function OptionalBadge() {
  const { t } = useTranslation();

  return (
    <span className="rounded-full border border-[#8A6D23] px-2 py-0.5 font-sans text-[10px] font-normal uppercase text-[#8A6D23]">
      {t("label.optional", "Optional")}
    </span>
  );
}

function MenuButton({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();
  const label = t(
    isMenuOpen ? "navigation.closeMenu" : "navigation.openMenu",
  );

  return (
    <Button
      type="button"
      shape="circle"
      color="ghost"
      aria-label={label}
      title={label}
      onClick={() => setIsMenuOpen((previous) => !previous)}
      className={`h-[38px] min-h-[38px] w-[38px] border-2 border-primary p-2 text-primary hover:bg-primary/10 ${className}`}
    >
      {isMenuOpen ? (
        <XMarkIcon aria-hidden="true" />
      ) : (
        <Bars3Icon aria-hidden="true" />
      )}
    </Button>
  );
}

function MoreInfo(props: MoreInfoProps) {
  return (
    <TripLayoutProvider>
      <MoreInfoContent {...props} />
    </TripLayoutProvider>
  );
}

function MoreInfoContent({
  onComplete,
  onCompleteWhenDirty,
}: MoreInfoProps) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();
  const {
    nextStep,
    setContextData,
    contextData,
    previousStep,
    goToStep,
    steps,
  } =
    useStepperContext<typeof allTripCreateSteps, ExtendedTripData>();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handlePreviousStep = () => {
    if (
      !shouldCollectBookings(contextData) &&
      steps.includes(TripCreateStep.WITH_WHO)
    ) {
      goToStep(TripCreateStep.WITH_WHO);
      return;
    }

    previousStep();
  };

  const dna = user?.dna?.data;
  const notSet = t("tripCreateForm.moreInfo.notSet", "Not set");

  const pace = dna?.how_do_you_like_to_travel
    ? t(
        `travelDnaPage.howDoYouLikeToTravel.${dna.how_do_you_like_to_travel}`,
        {
          defaultValue: missingTranslationToReadable(
            dna.how_do_you_like_to_travel,
          ),
        },
      )
    : notSet;

  const spendingStyle = dna?.spending_preference
    ? t(
        `travelDnaPage.yourSpending.captions.${dna.spending_preference}`,
        {
          defaultValue: String(dna.spending_preference),
        },
      )
    : notSet;

  const accommodationPreference = dna?.what_type_of_place_do_you_prefer
    ? t(
        `travelDnaPage.whatTypeOfPlaceDoYouPrefer.${dna.what_type_of_place_do_you_prefer}`,
        {
          defaultValue: missingTranslationToReadable(
            dna.what_type_of_place_do_you_prefer,
          ),
        },
      )
    : notSet;

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<MoreInfoSchema>({
    resolver: zodResolver(moreInfoSchema),
    defaultValues: {
      trip_specific_changes: contextData?.trip_specific_changes ?? "",
      additional_requests: contextData?.additional_requests ?? "",
    },
  });

  const onSubmit = (formData: MoreInfoSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);

        const data: MoreInfoSchema = {
          trip_specific_changes:
            formData.trip_specific_changes?.trim() || null,
          additional_requests:
            formData.additional_requests?.trim() || "",
        };
        const nextContext: ExtendedTripData = {
          ...contextData,
          ...data,
        };
        const args: OnCompleteArgs = {
          step: TripCreateStep.MORE_INFO,
          data,
          context: nextContext,
        };

        if (isDirty) {
          setContextData(() => nextContext);
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        const submitError =
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError));

        setError(submitError);
        console.error(caughtError);
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <div className="drawer fixed inset-0 z-40 h-[100dvh] w-screen overflow-y-auto bg-journi-theme-light">
      <input
        id="more-info-navigation-drawer"
        type="checkbox"
        className="drawer-toggle"
        checked={isMenuOpen}
        onChange={(event) => setIsMenuOpen(event.target.checked)}
        aria-label={t("navigation.menu")}
      />

      <main className="drawer-content flex min-h-[100dvh] w-full flex-col bg-journi-theme-light px-5 py-6 text-[#191919] md:px-12 md:py-8 lg:px-20">
        <header className="flex w-full items-center gap-4 border-b border-primary/60 pb-6">
          <MenuButton />
          <span className="font-feature text-2xl font-bold md:text-3xl">
            {t("brand.name")}
          </span>
        </header>

        {error ? (
          <Alert status="error" className="mt-6">
            {error.message}
          </Alert>
        ) : null}

        <form
          className="mx-auto flex w-full max-w-[920px] flex-col py-10 md:py-12"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <p className="text-center font-sans text-sm font-normal uppercase tracking-wide text-primary md:text-base">
            {t(
              "tripCreateForm.moreInfo.eyebrow",
              "This trip, specifically",
            )}
          </p>

          <h1 className="mt-4 font-feature text-3xl font-normal leading-tight md:text-4xl">
            {t(
              "tripCreateForm.moreInfo.titleStart",
              "Should anything be",
            )}{" "}
            <span className="italic text-primary">
              {t("tripCreateForm.moreInfo.titleHighlight", "different")}
            </span>{" "}
            {t("tripCreateForm.moreInfo.titleEnd", "this time?")}
          </h1>

          <p className="mt-3 font-sans text-sm font-normal italic text-text-secondary md:text-base">
            {t(
              "tripCreateForm.moreInfo.description",
              "Your Travel DNA stays the default. Only tell Cody what changes for this trip.",
            )}
          </p>

          <div className="mt-8 w-full max-w-[920px] rounded-[14px] border border-[#8A6D23] bg-white px-5 py-2 sm:px-6">
            <SummaryRow
              label={t("tripCreateForm.moreInfo.pace", "Pace")}
              value={pace}
            />
            <SummaryRow
              label={t(
                "tripCreateForm.moreInfo.spendingStyle",
                "Spending style",
              )}
              value={spendingStyle}
            />
            <SummaryRow
              label={t(
                "tripCreateForm.moreInfo.accommodationPreference",
                "Accommodation preference",
              )}
              value={accommodationPreference}
              isLast
            />
          </div>

          <div className="mt-10 flex w-full max-w-[920px] flex-col gap-10">
            <Controller
              control={control}
              name="trip_specific_changes"
              render={({ field, fieldState }) => (
                <label className="block">
                  <span className="flex flex-wrap items-center gap-4 font-sans text-sm font-normal text-primary md:text-base">
                    {t(
                      "tripCreateForm.moreInfo.whatShouldBeDifferent",
                      "What should be different?",
                    )}
                    <OptionalBadge />
                  </span>
                  <textarea
                    rows={6}
                    maxLength={1000}
                    value={field.value ?? ""}
                    disabled={isPending}
                    placeholder={t(
                      "tripCreateForm.moreInfo.differentPlaceholder",
                      "e.g. Slower than usual, smaller hotels and more time by the water...",
                    )}
                    className="mt-3 min-h-[180px] w-full resize-y rounded-[14px] border border-[#8A6D23] bg-white px-4 py-3 font-sans text-base font-normal text-[#191919] outline-none placeholder:font placeholder:text-text-secondary focus:ring-2 focus:ring-primary/20"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                  {fieldState.error?.message ? (
                    <span className="mt-2 block font-sans text-sm text-error">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </label>
              )}
            />

            <Controller
              control={control}
              name="additional_requests"
              render={({ field, fieldState }) => (
                <label className="block">
                  <span className="flex flex-wrap items-center gap-4 font-sans text-sm font-normal text-primary md:text-base">
                    {t(
                      "tripCreateForm.moreInfo.mustInclude",
                      "One thing this trip must include",
                    )}
                    <OptionalBadge />
                  </span>
                  <textarea
                    rows={6}
                    maxLength={1000}
                    value={field.value ?? ""}
                    disabled={isPending}
                    placeholder={t(
                      "tripCreateForm.moreInfo.mustIncludePlaceholder",
                      "e.g. A spa day, a special dinner or one full day by the sea...",
                    )}
                    className="mt-3 min-h-[180px] w-full resize-y rounded-[14px] border border-[#8A6D23] bg-white px-4 py-3 font-sans text-base font-normal text-[#191919] outline-none placeholder:font placeholder:text-text-secondary focus:ring-2 focus:ring-primary/20"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                  {fieldState.error?.message ? (
                    <span className="mt-2 block font-sans text-sm text-error">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </label>
              )}
            />
          </div>

          <div className="mt-10 flex w-full max-w-[920px] flex-col items-center gap-3 pb-2">
            <LoadingButton
              loading={isPending}
              type="submit"
              color="primary"
              className="journi-primary-button w-full sm:w-[360px]"
              endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
            >
              {t(
                "tripCreateForm.moreInfo.reviewBeforeBuilding",
                "Review before building",
              )}
            </LoadingButton>

            <button
              type="button"
              className="min-h-11 px-6 font-sans text-base font-semibold text-[#191919] hover:text-primary"
              onClick={handlePreviousStep}
            >
              {t("button.goBack")}
            </button>
          </div>
        </form>
      </main>

      <div className="drawer-side z-50">
        <label
          htmlFor="more-info-navigation-drawer"
          className="drawer-overlay"
          aria-label={t("navigation.closeMenu")}
        />

        <aside className="flex h-[100dvh] w-[390px] max-w-[92vw] flex-col overflow-hidden border-r border-primary/15 bg-journi-theme-light shadow-2xl">
          <header className="flex shrink-0 items-center justify-between border-b border-primary/20 px-6 py-5">
            <span className="font-feature text-2xl font-bold text-base-content">
              {t("brand.name")}
            </span>

            <MenuButton className="border-primary/30 hover:border-primary" />
          </header>

          <div className="min-h-0 flex-1 px-5 py-5">
            <Navbar className="h-full min-h-0 w-full" />
          </div>
        </aside>
      </div>
    </div>
  );
}

export default MoreInfo;
